import {
    mapDefaultZoom,
    mapMaxZoom,
    mapboxStyleURL,
} from '@/features/orders/components/MapPanel/parts/Content/constants'
import { MapProps } from '@/features/orders/components/MapPanel/parts/Content/parts/Map/types'
import { loadMapbox } from '@/features/orders/components/MapPanel/parts/Content/parts/Map/utils/loadMapbox.web'
import { registerPinImages } from '@/features/orders/components/MapPanel/parts/Content/parts/Map/utils/registerPinImages.web'
import { requireMapboxAccessToken } from '@/features/orders/components/MapPanel/parts/Content/parts/Map/utils/requireMapboxAccessToken'
import { MapCoordinate } from '@/features/orders/components/MapPanel/parts/Content/types'
import { useLatestValueRef } from '@/hooks/useLatestValueRef'
import type mapboxgl from 'mapbox-gl'
import { useEffect, useRef, useState } from 'react'

interface UseMapInstanceOptions extends Pick<MapProps, 'onError'> {
    initialCenter: MapCoordinate | undefined
    language: string
}

export const useMapInstance = ({ initialCenter, language, onError }: UseMapInstanceOptions) => {
    const mapboxAccessToken = requireMapboxAccessToken('web')

    const container = useRef<HTMLDivElement>(null)
    const [map, setMap] = useState<mapboxgl.Map>()
    const latestOnError = useLatestValueRef(onError)
    const initialCenterRef = useRef(initialCenter)
    const initialLanguageRef = useRef(language)

    useEffect(() => {
        const element = container.current
        if (!element) return

        const controller = new AbortController()
        const { signal } = controller
        let instance: mapboxgl.Map | undefined
        let observer: ResizeObserver | undefined

        const initialize = async () => {
            const { default: mapbox } = await loadMapbox()
            if (signal.aborted) return

            const createdMap = new mapbox.Map({
                container: element,
                accessToken: mapboxAccessToken,
                style: mapboxStyleURL,
                center: initialCenterRef.current,
                zoom: mapDefaultZoom,
                maxZoom: mapMaxZoom,
                language: initialLanguageRef.current,
                attributionControl: true,
                fadeDuration: 0,
            })
            instance = createdMap
            createdMap.dragRotate.disable()
            createdMap.touchZoomRotate.disableRotation()
            createdMap.addControl(new mapbox.NavigationControl({ showCompass: false }), 'top-right')
            createdMap.on('load', async () => {
                try {
                    if (signal.aborted) return

                    await registerPinImages(createdMap, signal)

                    if (signal.aborted) return

                    setMap(createdMap)
                } catch (error) {
                    if (!signal.aborted) latestOnError.current(error)
                }
            })
            createdMap.on('error', (event) => {
                if (!signal.aborted) latestOnError.current(event)
            })

            observer = new ResizeObserver(() => createdMap.resize())
            observer.observe(element)
        }

        void initialize().catch((error: unknown) => {
            if (!signal.aborted) latestOnError.current(error)
        })

        return () => {
            controller.abort()
            observer?.disconnect()
            instance?.remove()
        }
    }, [latestOnError, mapboxAccessToken])

    useEffect(() => {
        if (map && map.getLanguage() !== language) map.setLanguage(language)
    }, [language, map])

    return { container, map }
}
